/**
 * 写真まとめて一括投稿ツールの位置手動指定 (issue #23、Phase C)。
 *
 * 位置情報のない写真に対し、地図クリック (またはマーカードラッグ) で位置を指定する小型 map。
 * `PostLocationPickerImpl`(user/map) と同じ imperative maplibre パターン:
 *   - GSI 淡色ラスタタイル (style version 8、pmtiles 非依存)
 *   - 地図 click → 単一の draggable マーカーを配置 / 移動 → onChange(lngLat)
 *   - マーカーをドラッグ → dragend で onChange(lngLat)
 *   - value があればそこにマーカーを立てて中心を合わせ、無ければ defaultCenter (なければ日本全体)
 *   - 現在の lat/lng を下部にテキスト表示
 *
 * jsdom / test 環境では WebGL が不完全なため、map 生成・操作は try/catch + window guard で
 * 防御し、import しても test が落ちないようにする。
 */
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef } from 'react';

const MARKER_COLOR = '#D97706';

/** 日本全体のデフォルト中心 / zoom (value も defaultCenter も無いとき)。 */
const DEFAULT_CENTER: [number, number] = [139.7, 35.68];
const DEFAULT_ZOOM = 5;
/** value / defaultCenter が指定されているときの寄り (建物が分かる程度)。 */
const POINT_ZOOM = 16;

const GSI_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    gsi: {
      type: 'raster',
      tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '地理院タイル',
    },
  },
  layers: [{ id: 'gsi', type: 'raster', source: 'gsi' }],
};

export interface LatLng {
  latitude: number;
  longitude: number;
}

export interface LocationPickerProps {
  value: LatLng | null;
  onChange: (loc: LatLng) => void;
  defaultCenter?: LatLng;
}

function makeMarkerElement(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    `width:24px;height:24px;border-radius:50%;background:${MARKER_COLOR};` +
    `border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4);cursor:grab;`;
  return el;
}

export function LocationPicker({ value, onChange, defaultCenter }: LocationPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  // onChange の最新参照 (map click / dragend handler 内で参照、再 mount 回避)。
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // 初期中心: value > defaultCenter > 日本全体。
  const initialCenter: [number, number] = value
    ? [value.longitude, value.latitude]
    : defaultCenter
      ? [defaultCenter.longitude, defaultCenter.latitude]
      : DEFAULT_CENTER;
  const initialZoom = value || defaultCenter ? POINT_ZOOM : DEFAULT_ZOOM;
  // 初期描画値は mount 時のみ使うため ref に固定 (props 変化で map を作り直さない)。
  const initialRef = useRef({ center: initialCenter, zoom: initialZoom });

  // マーカーを生成 or 移動する共通処理。
  const placeMarker = (map: maplibregl.Map, lngLat: [number, number]): void => {
    if (!markerRef.current) {
      const el = makeMarkerElement();
      const marker = new maplibregl.Marker({ element: el, draggable: true })
        .setLngLat(lngLat)
        .addTo(map);
      marker.on('dragend', () => {
        try {
          const { lng, lat } = marker.getLngLat();
          onChangeRef.current({ latitude: lat, longitude: lng });
        } catch {
          /* ignore */
        }
      });
      markerRef.current = marker;
    } else {
      markerRef.current.setLngLat(lngLat);
    }
  };

  // map 生成 (mount 時 1 回、unmount で map.remove())。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!containerRef.current || mapRef.current) return;
    try {
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: GSI_STYLE,
        center: initialRef.current.center,
        zoom: initialRef.current.zoom,
        // 位置指定用なので回転 / 傾きは無効化 (誤操作防止)。
        pitchWithRotate: false,
        dragRotate: false,
      });
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      map.on('load', () => {
        try {
          map.resize();
        } catch {
          /* jsdom: resize 不可 */
        }
      });
      // 地図クリック → マーカー配置 / 移動 → onChange。
      map.on('click', (e: maplibregl.MapMouseEvent) => {
        const { lng, lat } = e.lngLat;
        placeMarker(map, [lng, lat]);
        onChangeRef.current({ latitude: lat, longitude: lng });
      });
      mapRef.current = map;
      // value が初期から有るなら最初からマーカーを立てる。
      if (value) placeMarker(map, [value.longitude, value.latitude]);
    } catch {
      /* jsdom / WebGL 非対応: map 生成失敗は無視 (test 環境) */
    }
    return () => {
      try {
        markerRef.current?.remove();
      } catch {
        /* ignore */
      }
      markerRef.current = null;
      try {
        mapRef.current?.remove();
      } catch {
        /* ignore */
      }
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // value 変化時にマーカー + 中心を追従 (外部から位置が変わったとき)。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !value) return;
    const lngLat: [number, number] = [value.longitude, value.latitude];
    try {
      placeMarker(map, lngLat);
      map.easeTo({ center: lngLat, zoom: Math.max(map.getZoom(), POINT_ZOOM), duration: 0 });
    } catch {
      /* jsdom / WebGL 非対応: 無視 */
    }
    // placeMarker は ref のみ参照する安定処理のため依存に含めない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.latitude, value?.longitude]);

  return (
    <div>
      <div
        ref={containerRef}
        className="w-full h-72 rounded-lg border border-slate-200 overflow-hidden"
        aria-label="地図をクリックして位置を指定"
        data-testid="location-picker-map"
      />
      <p className="mt-1 text-xs text-slate-500">
        {value
          ? `緯度 ${value.latitude.toFixed(6)} / 経度 ${value.longitude.toFixed(6)}`
          : '地図をクリックして位置を指定してください。'}
      </p>
    </div>
  );
}
